import React from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, AlertCircle, TrendingUp } from 'lucide-react';
import { Button } from './ui/button';

/**
 * Usage Warning Banner
 * 
 * Displays warning banners at different usage thresholds:
 * - WARNING (70%): Subtle yellow banner
 * - HIGH_RISK (80%): Stronger orange banner
 * 
 * Does NOT display for HARD_STOP (that's handled by HardStopModal)
 */
export default function UsageWarningBanner({ warningLevel, costRatioPct, thresholdPct, planType, onDismiss }) {
  const navigate = useNavigate();
  
  // Don't show banner if no warning level or if it's HARD_STOP
  if (!warningLevel || warningLevel === 'HARD_STOP') {
    return null;
  }
  
  const isHighRisk = warningLevel === 'HIGH_RISK';
  
  const bgColor = isHighRisk 
    ? 'bg-orange-500/10 border-orange-500/30' 
    : 'bg-yellow-500/10 border-yellow-500/30';
  
  const textColor = isHighRisk ? 'text-orange-400' : 'text-yellow-400';
  const iconColor = isHighRisk ? 'text-orange-400' : 'text-yellow-400';
  
  const Icon = isHighRisk ? AlertCircle : AlertTriangle;
  
  const message = isHighRisk 
    ? "You're close to your usage limit. Consider upgrading to avoid interruption."
    : "You're approaching your usage limit.";
  
  const percentUsed = Math.round((costRatioPct / thresholdPct) * 100);
  
  return (
    <div className={`${bgColor} border rounded-lg p-3 mb-4`} data-testid="usage-warning-banner">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-3">
          <Icon className={`w-5 h-5 ${iconColor} flex-shrink-0 mt-0.5`} />
          <div className="flex-1">
            <p className={`text-sm font-medium ${textColor}`}>
              {message}
            </p>
            <p className="text-xs text-slate-400 mt-1">
              {costRatioPct.toFixed(1)}% of your {thresholdPct}% limit used 
              ({percentUsed}% capacity) • {planType} plan
            </p>
          </div>
        </div>
        
        <div className="flex items-center gap-2 flex-shrink-0">
          <Button
            size="sm"
            variant="ghost"
            onClick={() => navigate('/pricing')}
            className={`${textColor} hover:bg-white/10 text-xs`}
          >
            <TrendingUp className="w-3 h-3 mr-1" />
            Upgrade
          </Button>
          {onDismiss && (
            <button
              onClick={onDismiss}
              className="text-slate-500 hover:text-slate-300 text-xs"
            >
              Dismiss
            </button>
          )}
        </div>
      </div>
      
      {/* Progress bar */}
      <div className="mt-2 h-1.5 bg-slate-700/50 rounded-full overflow-hidden">
        <div 
          className={`h-full rounded-full transition-all duration-300 ${isHighRisk ? 'bg-orange-500' : 'bg-yellow-500'}`}
          style={{ width: `${Math.min(percentUsed, 100)}%` }}
        />
      </div>
    </div>
  );
}
